import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Clock, X } from "lucide-react";

/**
 * Picker d'heure inspiré du DateField — popover sur mesure plutôt que
 * `<input type="time">` natif (rendu hétérogène selon le navigateur).
 *
 * Plage métier : 6h → 22h, minutes par tranche de 5. Format de stockage
 * "HH:mm" en 24h (compatible avec les colonnes existantes edlHeure).
 */
interface TimeFieldProps {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  placeholder?: string;
  className?: string;
  highlight?: boolean;
  /** Heure min (incluse), 0-23. Défaut 6 */
  minHour?: number;
  /** Heure max (incluse), 0-23. Défaut 22 */
  maxHour?: number;
  /** Pas en minutes (1, 5, 10, 15, 30...). Défaut 5 */
  minuteStep?: number;
  /** Bord coloré quand le champ est vide. Aligne sur le pattern DateField. */
  missingHint?: "required" | "mailing";
  /**
   * Libellé SOUS le contrôle, en petit — pour les cases d'un `FieldGroup`, où
   * la légende porte déjà le sens. Même contrat que FormField/SelectField/DateField.
   */
  labelBelow?: boolean;
  /** Valeur centrée dans le contrôle (heures courtes alignées en groupe). */
  align?: "left" | "center";
  /**
   * Champ verrouillé : même boîte, même place, mais inerte.
   *
   * ⚠️ Ce composant l'ignorait jusqu'au 2026-08-10. Le helper `F()` des fiches
   * clone `readOnly` sur l'élément qu'on lui passe pour VERROUILLER un champ
   * qu'un rôle n'a pas le droit d'éditer ; TimeField ne connaissant pas la
   * prop, elle tombait dans le vide et « Heure » restait modifiable pour tout
   * le monde. Contrat identique à `DateField.readOnly`.
   */
  readOnly?: boolean;
}

function buildHours(min: number, max: number): string[] {
  const out: string[] = [];
  for (let h = min; h <= max; h++) out.push(String(h).padStart(2, "0"));
  return out;
}

function buildMinutes(step: number): string[] {
  const out: string[] = [];
  for (let m = 0; m < 60; m += step) out.push(String(m).padStart(2, "0"));
  return out;
}

export function TimeField({
  label,
  value = "",
  onChange,
  error,
  placeholder = "...",
  className,
  highlight,
  minHour = 6,
  maxHour = 22,
  minuteStep = 5,
  missingHint,
  labelBelow = false,
  align = "left",
  readOnly = false,
}: TimeFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hourColRef = useRef<HTMLDivElement>(null);
  const minuteColRef = useRef<HTMLDivElement>(null);
  // Popup en portail (document.body), positionné en `fixed` sur le rect du
  // déclencheur — un simple `position: absolute` se faisait couper par tout
  // ancêtre `overflow-y-auto` (ex. le panneau latéral étroit de Missions
  // prestataire, signalé Thibaud 2026-08-20 : "le calendrier et l'heure sont
  // coupés"). Même patron que PipelineActionsPopover.
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  const isFilled = value.length > 0;

  // Parse value "HH:mm" — tolère "" ou format invalide en revenant à null.
  const parsed = (() => {
    const m = /^(\d{2}):(\d{2})$/.exec(value);
    if (!m) return null;
    return { h: m[1], m: m[2] };
  })();

  const hours = buildHours(minHour, maxHour);
  const minutes = buildMinutes(minuteStep);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = !!ref.current && ref.current.contains(target);
      const insidePopup = !!popupRef.current && popupRef.current.contains(target);
      if (!insideTrigger && !insidePopup) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Position du popup portalé : calquée sur le rect du déclencheur à
  // l'ouverture, recalculée si la page défile/redimensionne pendant que le
  // popup est ouvert (le scroll capturé en phase capture attrape aussi le
  // scroll d'un conteneur ancêtre, pas seulement `window`).
  useEffect(() => {
    if (!isOpen) { setPopupPos(null); return; }
    // Même correctif que DateField (signalé Thibaud 2026-09-12) : bascule
    // au-dessus du déclencheur quand il n'y a pas la place en dessous.
    // Ancré par `bottom` côté "au-dessus" pour coller exactement au-dessus
    // sans dépendre d'une hauteur estimée.
    const ESTIMATED_HEIGHT = 240;
    const update = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const left = r.left;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      if (spaceBelow < ESTIMATED_HEIGHT && spaceAbove > spaceBelow) {
        setPopupPos({ left, bottom: window.innerHeight - r.top + 4 });
      } else {
        setPopupPos({ left, top: r.bottom + 4 });
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  // Scroll les colonnes pour centrer la valeur sélectionnée à l'ouverture.
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const scrollToSelected = (col: HTMLDivElement | null) => {
        if (!col) return;
        const selected = col.querySelector<HTMLElement>("[data-selected='true']");
        if (selected) {
          selected.scrollIntoView({ block: "center", behavior: "auto" });
        }
      };
      scrollToSelected(hourColRef.current);
      scrollToSelected(minuteColRef.current);
    });
  }, [isOpen]);

  const showMissingBorder = !readOnly && !isFilled && !isOpen && !error && !!missingHint;
  const missingBorderClass =
    missingHint === "required"
      ? "border border-solid border-danger"
      : missingHint === "mailing"
        ? "border border-solid border-warning"
        : "";

  const borderClasses = readOnly
    ? "border border-solid border-transparent"
    : error
    ? "border border-solid border-danger"
    : showMissingBorder
      ? missingBorderClass
      : isOpen
        ? "border border-solid border-border-focus"
        : isFilled && isHovered
          ? "border border-dashed border-border-hover"
          : isFilled
            ? "border border-solid border-transparent"
            : isHovered
              ? "border border-dashed border-border-hover"
              : "border border-dashed border-border";

  const updateTime = (h: string, m: string) => {
    onChange?.(`${h}:${m}`);
  };

  const onPickHour = (h: string) => {
    const m = parsed?.m ?? "00";
    updateTime(h, m);
  };

  const onPickMinute = (m: string) => {
    const h = parsed?.h ?? String(minHour).padStart(2, "0");
    updateTime(h, m);
  };

  const labelNode = (
    <label
      className={cn(
        "font-medium tracking-wider uppercase truncate",
        labelBelow ? "text-[9.5px] text-center mt-1 px-0.5" : "text-xs px-3 mb-1",
        highlight ? "text-danger" : "text-text-muted",
      )}
    >
      {label}
    </label>
  );

  return (
    <div className={cn("flex flex-col", className)} ref={ref}>
      {!labelBelow && labelNode}
      <div
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? undefined : 0}
        className={cn(
          "px-3 py-2 bg-surface-2 rounded-md text-sm",
          align === "center" ? "text-center" : "text-left",
          "outline-none shadow-none transition-colors duration-150",
          "flex items-center justify-between gap-2",
          readOnly ? "cursor-default" : "cursor-pointer",
          borderClasses,
          isFilled ? "text-text-primary" : "text-text-muted",
        )}
        onClick={() => !readOnly && setIsOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!readOnly && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsOpen((v) => !v);
          }
          if (e.key === "Escape") setIsOpen(false);
        }}
        onMouseEnter={() => !readOnly && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className={cn(align === "center" && "flex-1 text-center")}>
          {value || (readOnly ? "—" : placeholder)}
        </span>
        <div className="flex items-center gap-1">
          {isFilled && !readOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.("");
                setIsOpen(false);
              }}
              className="p-0.5 rounded hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Effacer l'heure"
              title="Effacer"
            >
              <X size={14} className="text-text-muted hover:text-danger" />
            </button>
          )}
          <Clock
            size={16}
            className={cn(
              "text-text-muted transition-colors duration-150",
              isOpen && "text-text-secondary",
            )}
          />
        </div>
      </div>
      {isOpen && !readOnly && popupPos && createPortal(
        <div
          ref={popupRef}
          style={{
            position: "fixed",
            left: popupPos.left,
            zIndex: 9999,
            ...(popupPos.top !== undefined ? { top: popupPos.top } : { bottom: popupPos.bottom }),
          }}
          className="bg-surface border border-border rounded-md shadow-lg p-2 flex gap-1"
        >
            {/* Hours column */}
            <div
              ref={hourColRef}
              className="max-h-[200px] overflow-y-auto flex flex-col"
            >
              {hours.map((h) => {
                const selected = parsed?.h === h;
                return (
                  <button
                    key={h}
                    type="button"
                    data-selected={selected || undefined}
                    className={cn(
                      "px-4 py-1.5 rounded text-sm cursor-pointer transition-colors min-w-[3rem] text-center",
                      selected
                        ? "bg-accent text-accent-foreground font-medium"
                        : "text-text-primary hover:bg-gray-200",
                    )}
                    onClick={() => onPickHour(h)}
                  >
                    {h}
                  </button>
                );
              })}
            </div>
            {/* Minutes column */}
            <div
              ref={minuteColRef}
              className="max-h-[200px] overflow-y-auto flex flex-col"
            >
              {minutes.map((m) => {
                const selected = parsed?.m === m;
                return (
                  <button
                    key={m}
                    type="button"
                    data-selected={selected || undefined}
                    className={cn(
                      "px-4 py-1.5 rounded text-sm cursor-pointer transition-colors min-w-[3rem] text-center",
                      selected
                        ? "bg-accent text-accent-foreground font-medium"
                        : "text-text-primary hover:bg-gray-200",
                    )}
                    onClick={() => onPickMinute(m)}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
        </div>,
        document.body,
      )}
      {labelBelow && labelNode}
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </div>
  );
}
